import { observer } from 'mobx-react-lite';
import type { ApplicationSdk } from '../../../core/sdk';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { useEffect } from 'react';
import KihubAdminStore from '../stores/KihubAdminStore';
import type { KihubDatabaseStore } from '../stores/KihubAdminStore';

const OpenAISettings = observer(({ store, sdk }: { store: any; sdk: ApplicationSdk }) => {
    const handleChangeApiKey = async () => {
        const result = await sdk.getDialogStringInput({
            title: 'API Key eingeben',
            message: 'Bitte geben Sie Ihren OpenAI API Key ein',
            initialValue: store.openai_api_key || '',
            label: 'API Key',
            onConfirm: async ({ input }: { input?: string }) => store.saveApiKey(input),
        });

        if (result.confirmed) {
            sdk.showSuccessToast('API-Key erfolgreich hinzugefügt');
        } else if (result.error) {
            sdk.showErrorToast('API-Key ungültig');
        }
    };

    return (
        <Card
            title="OpenAI API Konfiguration"
            style={{
                marginBottom: '1rem',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
            }}
        >
            <div className="p-fluid">
                {store.openai_has_key ? (
                    <div>
                        <Message
                            severity="success"
                            text="OpenAI API-Schlüssel ist konfiguriert"
                            style={{ marginBottom: '1.5rem' }}
                        />
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '1rem',
                                alignItems: 'center',
                            }}
                        >
                            <Button
                                label="API-Schlüssel ändern"
                                icon="pi pi-pencil"
                                onClick={handleChangeApiKey}
                                className="p-button-outlined"
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    fontSize: '1rem',
                                    borderRadius: '6px',
                                }}
                            />
                        </div>
                    </div>
                ) : (
                    <div>
                        <Message
                            severity="warn"
                            text="Kein OpenAI API-Schlüssel konfiguriert - API-Schlüssel ist erforderlich"
                            style={{ marginBottom: '1.5rem' }}
                        />
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '1rem',
                                alignItems: 'center',
                            }}
                        >
                            <Button
                                label="API-Schlüssel hinzufügen"
                                icon="pi pi-plus"
                                onClick={handleChangeApiKey}
                                className="p-button-primary"
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    fontSize: '1rem',
                                    borderRadius: '6px',
                                }}
                            />
                            <small
                                style={{
                                    color: '#666',
                                    fontSize: '0.85rem',
                                    textAlign: 'center',
                                }}
                            >
                                Sie benötigen einen OpenAI API-Schlüssel, um die KI-Funktionen zu
                                nutzen
                            </small>
                        </div>
                    </div>
                )}
            </div>
        </Card>
    );
});

const KihubDatabaseComponent = observer(
    ({
        store,
        sdk,
        kihubDatabase,
    }: {
        store: any;
        sdk: ApplicationSdk;
        kihubDatabase: KihubDatabaseStore;
    }) => {
        const isEnabled = kihubDatabase.enabled;

        const handleActivateDatabase = async () => {
            const activationResult = await sdk.getDialogWait({
                title: 'Wissensdatenbank aktivieren',
                message: 'Die Wissensdatenbank wird aktiviert. Dies kann einen Moment dauern.',
                onConfirm: async () => kihubDatabase.activate(store),
            });

            if (activationResult.confirmed) {
                sdk.showSuccessToast('Wissensdatenbank erfolgreich aktiviert.');
            } else if (activationResult.error) {
                sdk.showErrorToast(activationResult.error);
            }
        };

        return (
            <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Tag
                            value={isEnabled ? 'Aktiv' : 'Inaktiv'}
                            severity={isEnabled ? 'success' : 'warning'}
                            icon={isEnabled ? 'pi pi-check' : 'pi pi-times'}
                        />
                    </div>
                    <div>
                        <label
                            style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}
                        >
                            Name:
                        </label>
                        <p
                            style={{
                                margin: 0,
                                padding: '0.5rem',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px',
                                color: isEnabled ? '#333' : '#666',
                            }}
                        >
                            {kihubDatabase.label || 'Kein Name'}
                        </p>
                    </div>
                    <div>
                        <label
                            style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}
                        >
                            Beschreibung:
                        </label>
                        <p
                            style={{
                                margin: 0,
                                padding: '0.5rem',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px',
                                color: isEnabled ? '#333' : '#666',
                            }}
                        >
                            {kihubDatabase.description || 'Keine Beschreibung'}
                        </p>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                        {!isEnabled ? (
                            <Button
                                label="Aktivieren"
                                icon="pi pi-power-off"
                                onClick={handleActivateDatabase}
                                className="p-button-success"
                                // loading={kihubDatabase.loading}
                                style={{ padding: '0.5rem 1rem' }}
                            />
                        ) : (
                            <Button
                                label="Bearbeiten"
                                icon="pi pi-pencil"
                                onClick={() => kihubDatabase.edit(sdk, store)}
                                className="p-button-outlined"
                                style={{ padding: '0.5rem 1rem' }}
                            />
                        )}
                    </div>
                </div>
            </>
        );
    }
);

const KihubDatabasesOverview = observer(({ store, sdk }: { store: any; sdk: ApplicationSdk }) => {
    const kihubDatabases = store.kihubDatabases || [];

    return (
        <Card
            title="Wissensdatenbanken"
            style={{
                marginBottom: '1rem',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
            }}
        >
            <div className="p-fluid">
                {!kihubDatabases || kihubDatabases.length === 0 ? (
                    <Message
                        severity="info"
                        text="Keine Wissensdatenbanken verfügbar"
                        style={{ marginBottom: '1rem' }}
                    />
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        {kihubDatabases.map((kihubDb: any) => {
                            return (
                                <KihubDatabaseComponent
                                    key={kihubDb.id}
                                    store={store}
                                    kihubDatabase={kihubDb}
                                    sdk={sdk}
                                />
                            );
                        })}
                    </div>
                )}
            </div>
        </Card>
    );
});

const KihubAdminComponent = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    useEffect(() => {
        sdk.useStore(KihubAdminStore);
    }, [sdk]);

    const store = sdk.currentStore;

    if (!store || store.isLoading) {
        return (
            <div
                style={{
                    maxWidth: '800px',
                    margin: '0 auto',
                    padding: '2rem',
                    minHeight: '100vh',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <ProgressSpinner style={{ display: 'block', margin: '2rem auto' }} />
            </div>
        );
    }

    return (
        <div
            style={{
                maxWidth: '800px',
                margin: '0 auto',
                padding: '2rem',
                minHeight: '100vh',
            }}
        >
            <div style={{ marginBottom: '2rem' }}>
                <h1
                    style={{
                        fontSize: '2rem',
                        fontWeight: 'bold',
                        color: '#333',
                        textAlign: 'center',
                        marginBottom: '0.5rem',
                    }}
                >
                    KI-Hub Administration
                </h1>
                <p
                    style={{
                        textAlign: 'center',
                        color: '#666',
                        fontSize: '1.1rem',
                        marginBottom: '2rem',
                    }}
                >
                    Konfigurieren Sie hier Ihre OpenAI API-Einstellungen
                </p>
            </div>

            {store.error && (
                <Message
                    severity="error"
                    text={store.error}
                    style={{ marginBottom: '1.5rem' }}
                />
            )}

            <div className="p-fluid">
                <OpenAISettings
                    store={store}
                    sdk={sdk}
                />
                {store.openai_has_key && (
                    <KihubDatabasesOverview
                        store={store}
                        sdk={sdk}
                    />
                )}
            </div>
        </div>
    );
});

export default KihubAdminComponent;
